import { useReducer } from "react";

import type { PassengerGender } from "@app/constants/passengers";
import type { ValidationError } from "@app/helpers/input-validator";
import type { NapiPassenger, NapiUser } from "@app/types/napi-types";

import type { AnyAction } from "@reduxjs/toolkit";

export interface Field<T> {
  value: T;
  has_error?: boolean;
  error?: ValidationError;
}

export type PassengerFormType = Omit<NapiPassenger, "first_name"> & {
  first_name: Field<string>;
  last_name: Field<string>;
  gender?: Field<PassengerGender>;
  is_dirty: boolean;
};

export interface State {
  submit_status_user?: "success" | string;
  submit_status_passengers?: "success" | string;
  is_loading: boolean;
  account_holder: {
    id: number;
    first_name: Field<string>;
    last_name: Field<string>;
    is_dirty: boolean;
    full_name: string;
  };
  passengers: PassengerFormType[];
  new_passenger?: {
    first_name: Field<string>;
    last_name: Field<string>;
    gender?: Field<PassengerGender>;
  };
  passenger?: {};
}

const reducer = (
  state: State,
  action: AnyAction & { form: keyof State; field: keyof PassengerFormType }
): State => {
  switch (action.type) {
    case "error": {
      if (action.form === "passenger") {
        return {
          ...state,
          passengers: state.passengers.map(passenger => {
            if (passenger.id !== action.passenger.id) {
              return passenger;
            }

            return {
              ...passenger,
              [action.field]: {
                ...(passenger[action.field] as any),
                has_error: true,
                error: action.error
              }
            };
          })
        };
      }

      const state_action_form: any = state[action.form];
      return {
        ...state,
        [action.form]: {
          ...state_action_form,
          [action.field]: {
            ...state_action_form?.[action.field],
            has_error: true,
            error: action.error
          }
        }
      };
    }
    case "change": {
      if (action.form === "passenger") {
        return {
          ...state,
          passengers: state.passengers.map(passenger => {
            if (passenger.id !== action.passenger_id) {
              return passenger;
            }

            return {
              ...passenger,
              [action.field]: {
                value: action.value
              },
              is_dirty: true
            };
          })
        };
      }

      return {
        ...state,
        [action.form]: {
          ...(state[action.form] as any),
          [action.field]: {
            value: action.value
          },
          is_dirty: true
        }
      };
    }
    case "update_user": {
      return {
        ...state,
        is_loading: false,
        submit_status_user: "success",
        account_holder: { ...state.account_holder, is_dirty: false }
      };
    }
    case "update_passenger": {
      return {
        ...state,
        submit_status_user: "success",
        is_loading: false,
        passengers: state.passengers.map(passenger => {
          if (passenger.id !== action.passenger.id) {
            return passenger;
          }
          return {
            ...passenger,
            is_dirty: false
          };
        })
      };
    }
    case "create_passenger":
      return {
        ...state,
        new_passenger: {
          first_name: { value: "" },
          last_name: { value: "" },
          gender: undefined
        }
      };
    case "add_passenger": {
      const { new_passenger, ...rest } = state;
      return {
        ...rest,
        is_loading: false,
        submit_status_user: "success",
        passengers: [
          ...state.passengers,
          { ...action.passenger, is_dirty: false }
        ]
      };
    }
    case "delete_new_passenger": {
      const { new_passenger, ...rest } = state;
      return rest;
    }
    case "delete_passenger": {
      const new_passengers = state.passengers.filter(
        p => p.id !== action.passenger_id
      );
      return {
        ...state,
        is_loading: false,
        passengers: new_passengers
      };
    }
    case "load":
      return { ...state, is_loading: true };
    case "reset_submit": {
      const { submit_status_user, submit_status_passengers, ...rest_state } =
        state;
      return rest_state;
    }
    case "submit_error_user": {
      return {
        ...state,
        is_loading: false,
        submit_status_user: action.error_type
      };
    }
    case "submit_error_passengers": {
      return {
        ...state,
        is_loading: false,
        submit_status_passengers: action.error_type
      };
    }
    default:
      return state;
  }
};

const convertPassengerToForm = (
  passenger: NapiPassenger | NapiUser,
  is_user = false
) => ({
  ...passenger,
  first_name: { value: passenger.first_name },
  last_name: { value: passenger.last_name },
  ...(!is_user && passenger.gender && { gender: { value: passenger.gender } }),
  is_dirty: false
});

export const usePassengerForm = ({
  user,
  passengers: passenger_props
}: {
  user: NapiUser;
  passengers: NapiPassenger[];
}) => {
  const initial_state: State = {
    is_loading: false,
    account_holder: {
      ...user,
      ...convertPassengerToForm(user, true),
      full_name: `${user.first_name} ${user.last_name}`
    },
    // @ts-expect-error
    passengers: passenger_props.map(p => convertPassengerToForm(p))
  };

  const [state, dispatch] = useReducer(reducer, initial_state);

  return [state, dispatch];
};
